import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  Filter,
  HelpCircle,
  MessageSquare,
  CheckCircle2,
  Clock,
  User,
  Calendar,
  Tag,
  ChevronDown,
  ChevronUp,
  AlertTriangle
} from 'lucide-react';
import { TeamQuestionService } from '@/services/team-question.service';
import { OpenQuestion, QuestionStatus, QuestionPriority, QuestionStatusLabels, QuestionPriorityLabels } from '@/models/team-question.model';

export const QuestionsBoard: React.FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<OpenQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<QuestionStatus | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<QuestionPriority | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const [answerText, setAnswerText] = useState<Record<string, string>>({});

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const allQuestions = await TeamQuestionService.getAllQuestions();
      setQuestions(allQuestions);
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredQuestions = () => {
    return questions.filter(question => {
      const matchesSearch = searchTerm === '' ||
        question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (question.context && question.context.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = filterStatus === 'all' || question.status === filterStatus;
      const matchesPriority = filterPriority === 'all' || question.priority === filterPriority;

      return matchesSearch && matchesStatus && matchesPriority;
    });
  };

  const getStatusBadge = (status: QuestionStatus) => {
    const badges = {
      [QuestionStatus.draft]: { color: 'bg-gray-100 text-gray-800', icon: Clock },
      [QuestionStatus.open]: { color: 'bg-blue-100 text-blue-800', icon: HelpCircle },
      [QuestionStatus.investigating]: { color: 'bg-yellow-100 text-yellow-800', icon: MessageSquare },
      [QuestionStatus.answered]: { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      [QuestionStatus.deferred]: { color: 'bg-purple-100 text-purple-800', icon: Clock },
      [QuestionStatus.invalidated]: { color: 'bg-gray-100 text-gray-600', icon: AlertTriangle },
    };
    return badges[status];
  };

  const getPriorityBadge = (priority: QuestionPriority) => {
    const badges = {
      [QuestionPriority.low]: 'bg-gray-100 text-gray-800',
      [QuestionPriority.medium]: 'bg-blue-100 text-blue-800',
      [QuestionPriority.high]: 'bg-orange-100 text-orange-800',
    };
    return badges[priority];
  };

  const toggleExpanded = (questionId: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  const handleSubmitAnswer = async (questionId: string) => {
    try {
      const answer = answerText[questionId];
      if (!answer || answer.trim() === '') return;

      await TeamQuestionService.answerQuestion(questionId, answer, 'current-user-id', 'Current User');
      setAnswerText({ ...answerText, [questionId]: '' });
      await loadQuestions();
    } catch (error) {
      console.error('Error submitting answer:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  const filteredQuestions = getFilteredQuestions();

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/team-workspace')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <HelpCircle className="w-8 h-8 mr-3 text-blue-600" />
              Open Questions
            </h1>
            <p className="text-gray-600 mt-1">Track and answer questions from meetings</p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Status:</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as QuestionStatus | 'all')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Statuses</option>
                  {Object.values(QuestionStatus).map((status) => (
                    <option key={status} value={status}>
                      {QuestionStatusLabels[status]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Priority:</label>
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value as QuestionPriority | 'all')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Priorities</option>
                  {Object.values(QuestionPriority).map((priority) => (
                    <option key={priority} value={priority}>
                      {QuestionPriorityLabels[priority]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Questions List */}
      <div className="space-y-4">
        {filteredQuestions.length === 0 ? (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-12 text-center">
            <HelpCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No questions found</p>
            <p className="text-gray-500 text-sm mt-2">Upload a meeting to extract questions</p>
          </div>
        ) : (
          filteredQuestions.map((question) => {
            const statusBadge = getStatusBadge(question.status);
            const StatusIcon = statusBadge.icon;
            const isExpanded = expandedQuestions.has(question.id);

            return (
              <div
                key={question.id}
                className="bg-white rounded-lg shadow border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Question Header */}
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.color} flex items-center`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {QuestionStatusLabels[question.status]}
                        </span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getPriorityBadge(question.priority)}`}>
                          {QuestionPriorityLabels[question.priority]} Priority
                        </span>
                        {question.containsPHI && (
                          <span className="px-2.5 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            PHI
                          </span>
                        )}
                      </div>

                      {/* Question */}
                      <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-start">
                        <HelpCircle className="w-5 h-5 mr-2 mt-1 text-blue-600 flex-shrink-0" />
                        {question.question}
                      </h3>

                      {/* Context */}
                      {question.context && (
                        <p className="text-gray-600 mb-4 ml-7">
                          {question.context}
                        </p>
                      )}

                      {/* Meta */}
                      <div className="flex items-center space-x-6 text-sm text-gray-500 ml-7">
                        {question.assignedTo && (
                          <div className="flex items-center">
                            <User className="w-4 h-4 mr-1" />
                            Assigned to: {question.assignedTo}
                          </div>
                        )}
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(question.createdAt).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <Tag className="w-4 h-4 mr-1" />
                          {question.category}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => toggleExpanded(question.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      )}
                    </button>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
                      {/* Answer */}
                      {question.answer && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-green-900 mb-1">Answer</h4>
                              <p className="text-gray-700 mb-2">{question.answer}</p>
                              {question.answeredBy && (
                                <p className="text-sm text-gray-600">
                                  Answered by {question.answeredBy}
                                  {question.answeredAt && ` on ${new Date(question.answeredAt).toLocaleDateString()}`}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Source Context */}
                      {question.sourceTextSnippet && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Source Context</h4>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 italic">"{question.sourceTextSnippet}"</p>
                            <p className="text-xs text-gray-500 mt-2">From: {question.meetingTitle}</p>
                          </div>
                        </div>
                      )}

                      {/* Answer Form */}
                      {(question.status === QuestionStatus.open || question.status === QuestionStatus.investigating) && !question.answer && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Provide Answer</h4>
                          <div className="space-y-3">
                            <textarea
                              value={answerText[question.id] || ''}
                              onChange={(e) => setAnswerText({ ...answerText, [question.id]: e.target.value })}
                              placeholder="Type your answer here..."
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              rows={4}
                            />
                            <button
                              onClick={() => handleSubmitAnswer(question.id)}
                              disabled={!answerText[question.id] || answerText[question.id].trim() === ''}
                              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Submit Answer
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Total Questions</div>
          <div className="text-2xl font-bold text-gray-900">{questions.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Open</div>
          <div className="text-2xl font-bold text-blue-600">
            {questions.filter(q => q.status === QuestionStatus.open).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Investigating</div>
          <div className="text-2xl font-bold text-yellow-600">
            {questions.filter(q => q.status === QuestionStatus.investigating).length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="text-sm text-gray-600">Answered</div>
          <div className="text-2xl font-bold text-green-600">
            {questions.filter(q => q.status === QuestionStatus.answered).length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionsBoard;
